// Core Modules
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

// External Modules
import { catchError, Observable } from 'rxjs';

// Services
import { ConfigService } from '@core/services/config/config.service';
import { ErrorHandlerService } from '@core/services/error-handler/error-handler.service';

// Models
import { IToken, ITokenList, IUserDetails } from '@shared/models/auth-api-response.model';
import { IProducts } from '@shared/models/products/product-config.model';
import { IClientListResponse, IClientRoles, IUser, IUserListResponse, IUserParams } from '@user-management/models/user.model';

// Environment
import { environment } from '@env/environment';

@Injectable({ providedIn: 'root' })
export class ApiService {
  // Injectors
  private readonly configService = inject(ConfigService);
  private readonly errorHandlerService = inject(ErrorHandlerService);
  private readonly httpClient = inject(HttpClient);

  public clients(): Observable<Record<string, any>> {
    return this.httpClient.get<Record<string, any>>(`${this.configService.getAPIBaseUrl}/auth/me/clients`).pipe(catchError(this.errorHandlerService.errorHandler));
  }

  public createUser(user: IUser): Observable<IUser> {
    return this.httpClient.post<IUser>(`${this.configService.getAPIBaseUrl}/auth/users`, user);
  }

  public deleteToken(id: string): Observable<Record<string, any>> {
    return this.httpClient.delete<Record<string, any>>(`${this.configService.getAPIBaseUrl}/auth/me/tokens/${id}`).pipe(catchError(this.errorHandlerService.errorHandler));
  }

  public deleteUser(email: string): Observable<Record<string, any>> {
    return this.httpClient.delete<Record<string, any>>(`${this.configService.getAPIBaseUrl}/auth/users/${email}`);
  }

  public getClients(pageNum: number): Observable<IClientListResponse> {
    return this.httpClient.get<IClientListResponse>(`${this.configService.getAPIBaseUrl}/auth/clients?pageSize=${environment.CLIENTLIST}&page=${pageNum}`);
  }

  public getProductsBasedOnClientId(clientId: number): Observable<IProducts[]> {
    return this.httpClient.get<IProducts[]>(`${this.configService.getAPIBaseUrl}/auth/clients/${clientId}/products`);
  }

  public getRoles(): Observable<IClientRoles> {
    return this.httpClient.get<IClientRoles>(`${this.configService.getAPIBaseUrl}/auth/roles?pageSize=100`);
  }

  public getUser(email: string): Observable<IUser> {
    return this.httpClient.get<IUser>(`${this.configService.getAPIBaseUrl}/auth/users/${email}`);
  }

  public getUsers(userParam: IUserParams): Observable<IUserListResponse> {
    const { searchTerm, pageSize, page, sort } = userParam;
    const appUrl = `${this.configService.getAPIBaseUrl}/auth/users`;
    const queryParams = {
      ...(searchTerm && { searchTerm }),
      ...(pageSize && { pageSize }),
      ...(page && { page }),
      ...(sort && { sort }),
    };
    return this.httpClient.get<IUserListResponse>(appUrl, { params: new HttpParams().appendAll(queryParams) }).pipe(catchError(this.errorHandlerService.errorHandler));
  }

  public listTokens(): Observable<ITokenList> {
    return this.httpClient.get<ITokenList>(`${this.configService.getAPIBaseUrl}/auth/me/tokens`).pipe(catchError(this.errorHandlerService.errorHandler));
  }

  public me(): Observable<IUserDetails> {
    return this.httpClient.get<IUserDetails>(`${this.configService.getAPIBaseUrl}/auth/me`).pipe(catchError(this.errorHandlerService.errorHandler));
  }

  public newToken(duration: number | null = null): Observable<IToken> {
    return this.httpClient.post<IToken>(`${this.configService.getAPIBaseUrl}/auth/me/tokens`, { duration }).pipe(catchError(this.errorHandlerService.errorHandler));
  }

  public updateUser(user: IUser): Observable<IUser> {
    return this.httpClient.put<IUser>(`${this.configService.getAPIBaseUrl}/auth/users/${user.email}`, user);
  }
}
